import { NZAirports } from '../types/Locations';
import { ErrorInfo } from '../models/ForecastTimelineData';
import { apiClient } from './apiClient';

/**
 * Interface for individual fog prediction in the aggregated response
 */
interface FogPrediction {
  id?: number;
  location_id: number;
  horizon: number;
  predicted_at: string;
  generated_at: string;
  fog_predicted: boolean;
  fog_prediction_confidence?: number;
  visibility_threshold: number;
  prediction_threshold?: number;
  model_name?: string;
  location: string;
  time: string;
  predicted_fog: boolean;
}

/**
 * Interface for individual fog actual in the aggregated response
 */
interface FogActual {
  id?: number;
  location_id: number;
  forecast_time: string;
  visibility_level: number;
  source: string;
  location: string;
}

/**
 * Interface for aggregated predictions segment
 */
interface AggregatedPredictions {
  location: number;
  segment_time: string;
  horizon: number;
  predictions: FogPrediction[];
  fog_predicted: boolean;
  lowest_visibility_level: number | null;
  normalized_confidence?: number | null;
}

/**
 * Interface for aggregated actuals segment
 */
interface AggregatedActuals {
  location: number;
  segment_time: string;
  fog_actuals: FogActual[];
  available_visibility_thresholds: number[];
  fog_predicted: boolean;
  lowest_visibility_level: number | null;
  highest_visibility_level?: number | null;
  visibility_median: number | null;
  visibility_mean: number | null;
}

/**
 * Interface for the complete fog forecast response
 */
interface FogForecastResponse {
  requested_time: string;
  aggregated_predictions: AggregatedPredictions[];
  aggregated_actuals: AggregatedActuals[];
  errors?: ErrorInfo[];
}

/**
 * Interface for the API response from the simplified /predictions/{location_id} endpoint
 */
export interface AirportPredictionsResponse {
  location: string; // ICAO code
  predictions: Array<{
    location: string;
    time: string;
    predicted_fog: boolean;
    visibility_threshold: number;
    prediction_confidence?: number;
    prediction_threshold?: number;
    prediction_horizon?: number;
  }>;
}

/**
 * Interface for batch forecast result with failure tracking
 */
export interface BatchFogForecastResult {
  forecasts: FogForecastResponse[];
  requestedCount: number;
  successCount: number;
  failedCount: number;
  hasPartialFailure: boolean;
}

/**
 * Service for fetching fog forecasts from the API
 * Now uses centralized Axios client for standardized HTTP communication
 */
class FogForecastService {
  constructor() {
    // API base URL is now handled by the centralized apiClient
    console.log('FogForecastService initialized with centralized API client');
  }

  /**
   * Fetches a fog forecast for a specific location
   * @param location The location ID from NZAirports enum
   * @param customNow Optional custom date to use instead of current time
   * @returns A promise resolving to the fog forecast response
   */
  public async getFogForecast(location: NZAirports, customNow?: Date | null): Promise<FogForecastResponse> {
    try {
      // Log the input date for debugging
      if (customNow) {
        console.log(`FogForecastService received customNow: ${customNow.toISOString()}`);
      }

      // Get requested time - use custom date if provided, otherwise use current time
      const requestedTime = customNow ? customNow.toISOString() : new Date().toISOString();

      console.log(`Fetching fog forecast for location ${location} with requested_time: ${requestedTime}`);

      // Make the API request using centralized Axios client
      const data: FogForecastResponse = await apiClient.get(
        `/api/fog-forecast/`,
        {
          params: {
            location: location,
            requested_time: requestedTime
          }
        }
      );

      console.log(`Received forecast with ${data.aggregated_predictions.length} prediction segments and ${data.aggregated_actuals.length} actual segments`);
      console.log(`Backend used requested_time: ${data.requested_time}`);

      // Log errors if present
      if (data.errors && data.errors.length > 0) {
        console.log(`Forecast response contains ${data.errors.length} errors:`, data.errors);
        // Log the complete response for debugging when errors are present
        console.log('Complete forecast response (with errors):', data);
      }

      return data;
    } catch (error) {
      console.error('Error fetching fog forecast:', error);
      throw error;
    }
  }

  /**
   * Split an array into balanced chunks with smaller chunks first
   * Example: 21 items with chunkSize 5 → [4,4,4,4,5] instead of [5,5,5,5,1]
   */
  private chunkArray<T>(array: T[], chunkSize: number): T[][] {
    if (array.length === 0) return [];

    const numChunks = Math.ceil(array.length / chunkSize);
    const baseSize = Math.floor(array.length / numChunks);
    const remainder = array.length % numChunks;

    const chunks: T[][] = [];
    let currentIndex = 0;

    for (let i = 0; i < numChunks; i++) {
      const currentChunkSize = i < (numChunks - remainder) ? baseSize : baseSize + 1;
      chunks.push(array.slice(currentIndex, currentIndex + currentChunkSize));
      currentIndex += currentChunkSize;
    }

    return chunks;
  }

  /**
   * Fetches fog forecasts for multiple locations using parallel chunked requests
   * @param locations Array of location IDs from NZAirports enum
   * @param customNow Optional custom date to use instead of current time
   * @param onChunkReceived Optional callback invoked as each chunk completes (for progressive UI updates)
   * @param chunkSize Number of locations to fetch per chunk (default: 5)
   * @returns A promise resolving to batch forecast result with failure tracking
   */
  public async getBatchFogForecast(
    locations: NZAirports[],
    customNow?: Date | null,
    onChunkReceived?: (chunk: FogForecastResponse[]) => void,
    chunkSize: number = 5
  ): Promise<BatchFogForecastResult> {
    try {
      // Get requested time - use custom date if provided, otherwise use current time
      const requestedTime = customNow ? customNow.toISOString() : new Date().toISOString();

      console.log(`Fetching batch fog forecast for ${locations.length} locations with requested_time: ${requestedTime} (chunk size: ${chunkSize})`);

      // Split locations into chunks
      const chunks = this.chunkArray(locations, chunkSize);
      console.log(`Split into ${chunks.length} chunks for parallel fetching`);

      // Fetch all chunks in parallel using Promise.allSettled for partial failure handling
      const allForecasts: FogForecastResponse[] = [];
      let failedChunks = 0;

      const results = await Promise.allSettled(
        chunks.map(async (chunk, chunkIndex) => {
          // Build query params for this chunk
          const params = new URLSearchParams();
          chunk.forEach(loc => params.append('locations', loc.toString()));
          params.append('requested_time', requestedTime);

          // Make the API request for this chunk
          const response: { forecasts: FogForecastResponse[] } = await apiClient.get(
            `/api/fog-forecast/batch?${params.toString()}`
          );

          console.log(`Chunk ${chunkIndex + 1}/${chunks.length} received: ${response.forecasts.length} forecasts`);

          // Add to results
          allForecasts.push(...response.forecasts);

          // Call callback if provided (for progressive UI updates)
          if (onChunkReceived) {
            try {
              onChunkReceived(response.forecasts);
            } catch (callbackError) {
              console.error(`Error in onChunkReceived callback for chunk ${chunkIndex + 1}:`, callbackError);
            }
          }

          return response.forecasts.length;
        })
      );

      // Log any failed chunks
      results.forEach((result, index) => {
        if (result.status === 'rejected') {
          failedChunks++;
          console.error(`Chunk ${index + 1}/${chunks.length} failed:`, result.reason);
        }
      });

      if (failedChunks > 0) {
        console.warn(`⚠️ ${failedChunks}/${chunks.length} chunks failed. Returned ${allForecasts.length} forecasts from successful chunks.`);
      } else {
        console.log(`✅ All chunks received: ${allForecasts.length} total forecasts`);
      }

      return {
        forecasts: allForecasts,
        requestedCount: locations.length,
        successCount: allForecasts.length,
        failedCount: locations.length - allForecasts.length,
        hasPartialFailure: failedChunks > 0
      };
    } catch (error) {
      console.error('Error fetching batch fog forecast:', error);
      throw error;
    }
  }
}

// Create a singleton instance
const fogForecastService = new FogForecastService();

// Export the service instance and types
export { fogForecastService };
export type { FogForecastResponse, AggregatedPredictions, AggregatedActuals, FogActual, FogPrediction };
